import { useState, useRef, useCallback, useEffect } from 'react';

// Debug flag - set to true to enable console logging
const DEBUG = false;
const log = (...args) => DEBUG && console.log(...args);

/**
 * Simple audio streaming - one stream at a time, immediate playback
 * Back to the working simple approach, but with proper queue support
 */
export function useAudioStreaming() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStreamId, setCurrentStreamId] = useState(null);
  const [queuedStreams, setQueuedStreams] = useState([]);
  const [error, setError] = useState(null);
  const [currentPlaybackText, setCurrentPlaybackText] = useState(''); // Text being spoken

  // Simple refs
  const streamsRef = useRef({}); // { streamId: { chunks: [], completed: false, text: '' } }
  const queueRef = useRef([]); // [streamId1, streamId2, ...]
  const currentAudioRef = useRef(null);
  const isProcessingRef = useRef(false);
  const isPlayingRef = useRef(false); // Use ref for synchronous checks to avoid race conditions

  /**
   * Initialize audio context
   */
  const initAudioContext = useCallback(async () => {
    try {
      if (!window.audioContext) {
        window.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (window.audioContext.state === 'suspended') {
        await window.audioContext.resume();
      }
      log('🎵 Audio context ready');
    } catch (error) {
      console.error('Audio context failed:', error);
    }
  }, []);

  /**
   * Play next available chunk or stream
   */
  const processQueue = useCallback(() => {
    log(`🔍 processQueue called - isProcessing: ${isProcessingRef.current}, isPlayingRef: ${isPlayingRef.current}`);

    // Use refs for synchronous checks to avoid race conditions
    if (isProcessingRef.current || isPlayingRef.current) {
      log(`⏸️ Already processing or playing, skipping`);
      return; // Already processing or playing
    }

    const nextStreamId = queueRef.current[0];
    if (!nextStreamId) {
      log('✅ Queue empty');
      return;
    }

    const stream = streamsRef.current[nextStreamId];
    if (!stream) {
      console.warn(`Stream ${nextStreamId} not found, removing from queue`);
      queueRef.current.shift();
      setQueuedStreams([...queueRef.current]);
      setTimeout(processQueue, 10);
      return;
    }

    // Check if stream is already playing or finished
    if (stream.isPlaying || stream.isFinished) {
      log(`Stream ${nextStreamId} already playing or finished, skipping`);
      return;
    }

    // Check if we have chunks to play
    if (stream.chunks.length === 0) {
      if (stream.completed) {
        log(`Stream ${nextStreamId} completed with no chunks, removing`);
        queueRef.current.shift();
        setQueuedStreams([...queueRef.current]);
        delete streamsRef.current[nextStreamId];
        setTimeout(processQueue, 10);
      } else {
        log(`Stream ${nextStreamId} waiting for chunks...`);
      }
      return;
    }

    // Start playing this stream
    log(`🚀 Starting playback of stream ${nextStreamId} (${stream.chunks.length} chunks)`);
    setCurrentStreamId(nextStreamId);
    isProcessingRef.current = true;
    isPlayingRef.current = true;
    playStreamChunks(nextStreamId);
  }, []);

  /**
   * Play all chunks from a stream sequentially
   */
  const playStreamChunks = useCallback((streamId) => {
    log(`🔍 playStreamChunks called for ${streamId}`);

    const stream = streamsRef.current[streamId];
    if (!stream || stream.chunks.length === 0) {
      console.warn(`No chunks for stream ${streamId}`);
      finishStream(streamId);
      return;
    }

    // Prevent multiple calls for the same stream
    if (stream.isPlaying) {
      console.warn(`Stream ${streamId} is already playing, ignoring`);
      return;
    }
    stream.isPlaying = true;

    let chunkIndex = 0;

    const playNextChunk = () => {
      // Check if stream was stopped/finished externally
      const currentStream = streamsRef.current[streamId];
      if (!currentStream || currentStream.isFinished) {
        log(`Stream ${streamId} was stopped externally`);
        return;
      }

      if (chunkIndex >= currentStream.chunks.length) {
        log(`✅ All chunks played for stream ${streamId}`);
        finishStream(streamId);
        return;
      }

      const chunk = currentStream.chunks[chunkIndex];
      if (!chunk || !chunk.data) {
        console.warn(`Invalid chunk ${chunkIndex} for stream ${streamId}:`, chunk);
        chunkIndex++;
        setTimeout(playNextChunk, 10);
        return;
      }

      try {
        // Validate base64 data
        if (typeof chunk.data !== 'string' || chunk.data.length === 0) {
          console.warn(`Empty or invalid chunk data for chunk ${chunkIndex}`);
          chunkIndex++;
          setTimeout(playNextChunk, 10);
          return;
        }

        // Convert base64 to blob
        let binaryString;
        try {
          binaryString = atob(chunk.data);
        } catch (decodeError) {
          console.error(`Failed to decode base64 for chunk ${chunkIndex}:`, decodeError);
          chunkIndex++;
          setTimeout(playNextChunk, 10);
          return;
        }

        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const mimeType = `audio/${chunk.format || 'mp3'}`;
        const blob = new Blob([bytes], { type: mimeType });
        const audioUrl = URL.createObjectURL(blob);

        log(`🔊 Chunk ${chunkIndex + 1} prepared: ${bytes.length} bytes, format: ${mimeType}`);

        // Store reference to previous audio to clean up AFTER new one is ready
        const previousAudio = currentAudioRef.current;
        log(`🔊 Previous audio ref:`, previousAudio ? 'exists' : 'null');

        // Create audio element for this chunk
        const audio = new Audio();

        // Store reference immediately so it doesn't get garbage collected
        currentAudioRef.current = audio;

        // Use a flag to prevent oncanplaythrough from firing multiple times
        let hasStartedPlaying = false;
        let hasErrored = false;

        audio.onloadstart = () => {
          log(`🔊 Chunk ${chunkIndex + 1} load started`);
        };

        audio.onloadeddata = () => {
          log(`🔊 Chunk ${chunkIndex + 1} data loaded`);
        };

        audio.oncanplaythrough = () => {
          log(`🔊 Chunk ${chunkIndex + 1} canplaythrough fired, hasStartedPlaying: ${hasStartedPlaying}, hasErrored: ${hasErrored}`);
          if (hasStartedPlaying || hasErrored) return;
          hasStartedPlaying = true;

          // Clean up previous audio now that new one is ready
          if (previousAudio && previousAudio !== audio) {
            previousAudio.pause();
            previousAudio.src = '';
          }

          log(`🎵 Playing chunk ${chunkIndex + 1}/${currentStream.chunks.length} from stream ${streamId}`);
          setIsPlaying(true);
          isPlayingRef.current = true;
          audio.play().catch(err => {
            console.error(`Play error for chunk ${chunkIndex}:`, err);
            URL.revokeObjectURL(audioUrl);
            chunkIndex++;
            setTimeout(playNextChunk, 10);
          });
        };

        audio.onended = () => {
          log(`🔊 Chunk ${chunkIndex + 1} ended`);
          URL.revokeObjectURL(audioUrl);
          chunkIndex++;
          playNextChunk();
        };

        audio.onerror = (event) => {
          if (hasErrored) return;
          hasErrored = true;

          const audioError = audio.error;
          console.error(`Audio error for chunk ${chunkIndex}:`, {
            event,
            errorCode: audioError?.code,
            errorMessage: audioError?.message,
            networkState: audio.networkState,
            readyState: audio.readyState,
            src: audioUrl.substring(0, 50) + '...'
          });
          URL.revokeObjectURL(audioUrl);
          chunkIndex++;
          setTimeout(playNextChunk, 10);
        };

        // Set src after all event handlers are attached
        log(`🔊 Setting audio src for chunk ${chunkIndex + 1}`);
        audio.src = audioUrl;
        audio.preload = 'auto';

        // Force load to trigger events
        audio.load();

      } catch (error) {
        console.error(`Error playing chunk ${chunkIndex}:`, error);
        chunkIndex++;
        setTimeout(playNextChunk, 10);
      }
    };

    playNextChunk();
  }, []);

  /**
   * Finish current stream and move to next
   */
  const finishStream = useCallback((streamId) => {
    log(`🏁 Finished stream ${streamId}`);

    // Remove from queue
    const index = queueRef.current.indexOf(streamId);
    if (index > -1) {
      queueRef.current.splice(index, 1);
      setQueuedStreams([...queueRef.current]);
    }

    // Mark stream as finished but don't delete immediately (chunks might still arrive)
    const stream = streamsRef.current[streamId];
    if (stream) {
      stream.isFinished = true;
      stream.isPlaying = false;
    }

    // Reset all playing flags
    setIsPlaying(false);
    isPlayingRef.current = false;
    setCurrentStreamId(null);
    isProcessingRef.current = false;
    setCurrentPlaybackText(''); // Clear playback text when stream finishes

    // Clean up finished stream after a delay to handle late-arriving chunks
    setTimeout(() => {
      delete streamsRef.current[streamId];
      log(`🗑️ Cleaned up stream ${streamId}`);
    }, 2000);

    // Process next stream in queue (if any)
    if (queueRef.current.length > 0) {
      setTimeout(processQueue, 100);
    }
  }, [processQueue]);

  /**
   * Handle WebSocket messages
   */
  const handleStreamMessage = useCallback((message) => {
    setError(null);

    switch (message.type) {
      case 'audio_stream_start': {
        log(`🎵 Stream starting: ${message.streamId}`);

        // Check if this stream already exists (was auto-registered from early chunk)
        if (streamsRef.current[message.streamId]) {
          log(`📦 Stream ${message.streamId} already exists (auto-registered), updating`);
          // Stream was auto-registered, just make sure it's in the queue
          if (!queueRef.current.includes(message.streamId)) {
            queueRef.current.push(message.streamId);
            setQueuedStreams([...queueRef.current]);
          }
          break;
        }

        // Stop current audio for new stream (interrupting behavior)
        if (currentAudioRef.current) {
          log(`🛑 Stopping current audio for new stream`);
          currentAudioRef.current.pause();
          currentAudioRef.current.src = '';
          currentAudioRef.current = null;
        }

        // Reset playback state
        setIsPlaying(false);
        isPlayingRef.current = false;
        setCurrentStreamId(null);
        isProcessingRef.current = false;

        // Clear old streams and queue for fresh start
        queueRef.current = [];
        streamsRef.current = {};

        // Initialize new stream
        streamsRef.current[message.streamId] = {
          chunks: [],
          completed: false,
          isPlaying: false,
          isFinished: false,
          text: message.text || '' // Store the text being spoken
        };

        // Update current playback text
        if (message.text) {
          setCurrentPlaybackText(message.text);
        }

        // Add to queue
        queueRef.current.push(message.streamId);
        setQueuedStreams([...queueRef.current]);

        initAudioContext();
        break;
      }

      case 'audio_stream_info': {
        log(`📋 Stream info: ${message.streamId} (${message.totalChunks} chunks)`);
        break;
      }

      case 'audio_chunk': {
        let stream = streamsRef.current[message.streamId];

        // Auto-register stream if chunk arrives before stream_start (race condition)
        if (!stream) {
          log(`📦 Auto-registering stream ${message.streamId} from early chunk`);
          streamsRef.current[message.streamId] = {
            chunks: [],
            completed: false,
            isPlaying: false,
            isFinished: false
          };
          stream = streamsRef.current[message.streamId];

          // Add to queue if not already there
          if (!queueRef.current.includes(message.streamId)) {
            queueRef.current.push(message.streamId);
            setQueuedStreams([...queueRef.current]);
          }

          initAudioContext();
        }

        // Only accept chunks if stream is not finished
        if (!stream.isFinished) {
          stream.chunks.push(message);
          log(`📦 Chunk ${message.sequence + 1} received for ${message.streamId} (total: ${stream.chunks.length})`);

          // Try to start processing if nothing is playing yet
          if (queueRef.current[0] === message.streamId &&
              !isProcessingRef.current &&
              !isPlayingRef.current &&
              !stream.isPlaying) {
            log(`🚀 Chunk received, starting processing`);
            setTimeout(processQueue, 50);
          }
        } else {
          log(`📦 Ignoring late chunk for finished stream: ${message.streamId}`);
        }
        break;
      }

      case 'audio_stream_end': {
        const stream = streamsRef.current[message.streamId];
        if (stream) {
          stream.completed = true;
          log(`✅ Stream completed: ${message.streamId} (${stream.chunks.length} chunks)`);

          // Only start processing if nothing is playing and stream hasn't started yet
          if (queueRef.current[0] === message.streamId &&
              !isProcessingRef.current &&
              !isPlayingRef.current &&
              !stream.isPlaying) {
            log(`🚀 Stream completed and ready, starting processing`);
            setTimeout(processQueue, 50);
          }
        }
        break;
      }

      case 'audio_chunk_error':
      case 'audio_stream_error': {
        console.error(`❌ Error for stream ${message.streamId}:`, message.error);
        setError(message.error);
        break;
      }

      default:
        console.warn('Unknown audio message type:', message.type);
    }
  }, [processQueue, initAudioContext]);

  /**
   * Stop all audio
   */
  const stopAllStreams = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.src = '';
      currentAudioRef.current = null;
    }

    setIsPlaying(false);
    isPlayingRef.current = false;
    setCurrentStreamId(null);
    isProcessingRef.current = false;
    setCurrentPlaybackText('');
    queueRef.current = [];
    setQueuedStreams([]);
    streamsRef.current = {};
    log('🛑 Stopped all streams');
  }, []);

  /**
   * Test streaming functionality
   */
  const testStreaming = useCallback((sendMessage, text = "This is a test of real-time audio streaming.") => {
    if (!sendMessage) {
      console.error('No sendMessage function provided for streaming test');
      return;
    }

    log('🎵 Testing audio streaming with text:', text);
    sendMessage({
      type: 'test_streaming',
      text: text
    });
  }, []);

  /**
   * Send announcement (priority audio)
   */
  const sendAnnouncement = useCallback((sendMessage, text) => {
    if (!sendMessage) {
      console.error('No sendMessage function provided for announcement');
      return;
    }

    log('📢 Sending announcement:', text);
    sendMessage({
      type: 'send_announcement',
      text: text
    });
  }, []);

  /**
   * Stream response (general streaming)
   * @param {Function} sendMessage - WebSocket send function
   * @param {string} text - Text to stream as audio
   * @param {Object} options - Voice options
   * @param {Object} options.voice - Voice configuration { id, engine, speed }
   */
  const streamResponse = useCallback((sendMessage, text, options = {}) => {
    if (!sendMessage) {
      console.error('No sendMessage function provided for stream response');
      return;
    }

    const message = {
      type: 'stream_response',
      text: text,
      voice: options.voice
    };
    log('🎙️ Streaming response - full message:', JSON.stringify(message, null, 2));
    sendMessage(message);
  }, []);

  /**
   * Stop a specific stream
   */
  const stopStream = useCallback((streamId) => {
    log(`🛑 Stopping stream: ${streamId}`);

    if (currentStreamId === streamId && currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.src = '';
      currentAudioRef.current = null;
    }

    // Mark stream as finished to prevent further playback
    const stream = streamsRef.current[streamId];
    if (stream) {
      stream.isFinished = true;
      stream.isPlaying = false;
    }

    // Remove from queue
    const index = queueRef.current.indexOf(streamId);
    if (index > -1) {
      queueRef.current.splice(index, 1);
      setQueuedStreams([...queueRef.current]);
    }

    // Clean up stream data
    delete streamsRef.current[streamId];

    // If this was the current stream, reset state
    if (currentStreamId === streamId) {
      setIsPlaying(false);
      isPlayingRef.current = false;
      setCurrentStreamId(null);
      isProcessingRef.current = false;

      // Process next stream if available
      if (queueRef.current.length > 0) {
        setTimeout(processQueue, 100);
      }
    }
  }, [currentStreamId, processQueue]);

  /**
   * Get active streams info
   */
  const activeStreams = Object.keys(streamsRef.current).map(streamId => ({
    streamId,
    chunks: streamsRef.current[streamId]?.chunks?.length || 0,
    completed: streamsRef.current[streamId]?.completed || false
  }));

  const hasActiveStreams = activeStreams.length > 0;

  /**
   * Cleanup
   */
  useEffect(() => {
    return stopAllStreams;
  }, [stopAllStreams]);

  return {
    isPlaying,
    currentStreamId,
    queuedStreams,
    activeStreams,
    hasActiveStreams,
    currentPlaybackText, // Text currently being spoken (for echo cancellation)
    error,
    handleStreamMessage,
    stopAllStreams,
    stopStream,
    testStreaming,
    sendAnnouncement,
    streamResponse,
    clearError: () => setError(null)
  };
}